import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { LinkProps } from '@mui/material/Link';
import { grey, red } from '@mui/material/colors';
import { Theme, createTheme } from '@mui/material/styles';
import { isString } from 'lodash';
import React from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';

declare global {
    interface Window {
        theme: Theme;
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        minorLabel: React.CSSProperties;
    }

    // allow configuration using `createTheme()`
    interface TypographyVariantsOptions {
        minorLabel?: React.CSSProperties;
    }
    interface Palette {
        tip: Palette['primary'];
    }

    // allow configuration using `createTheme`
    interface PaletteOptions {
        tip?: PaletteOptions['primary'];
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        minorLabel: true;
    }
}

declare module '@mui/material/Alert' {
    interface AlertPropsColorOverrides {
        tip: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        tip: true;
    }
}

export const blue = {
    50: '#EFF7FE',
    100: '#BDDEFF',
    200: '#7CB9F6',
    300: '#3A94EE',
    400: '#096ED3', // primary sofar blue
    500: '#0A4C8D',
    600: '#002951',
    700: '#001931',
} as const;

export const borderGrey = '#c4c4c4';
export const iconGrey = '#7B8A9B';
export const minorTextGrey = grey[600];
export const cardBgLightGrey = '#f3f4f7';
export const headerBgLightBlue = blue[50];

export const mapAlertRed = red[500];
export const mapInfoBlue = blue[400];
export const mapLabelFontStack: [string, string, string] = [
    'Figtree Regular',
    'Helvetica Regular',
    'Arial Unicode MS Regular',
];

export const boldWeight = 500;

const LinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
    function ThemeLink(props, ref) {
        const { href, ...other } = props;
        const externalLink = isString(href) && href.match(/^http:|https:|mailto:/);
        // Map href (Material UI) -> to (react-router)
        return (
            <RouterLink
                ref={ref}
                to={href}
                target={externalLink ? '_blank' : undefined}
                rel={externalLink ? 'noopener noreferrer' : undefined}
                {...other}
            />
        );
    }
);

export const fontFamily = '"Figtree", "Helvetica", "Arial", sans-serif';

const theme = createTheme({
    palette: {
        /**
         * You might notice that primary, secondary, tip, and info are all the same shade of blue.
         *
         * History:
         * - primary used to be an almost-black blue that was (almost) never used
         * - secondary and info were different shades _close_ to sofar blue, which were used for most blue
         *   controls (but which one was fairly random)
         * - tip is used specifically as an alert severity; it is deliberately very similar to info, but the
         *   box and accents are blue instead instead of gray
         *
         * Goal:
         * - primary is used for most controls, and can be omitted for e.g. Button, TextField, Switch where
         *   it is the default
         * - secondary shouldn't generally be used - we use outline/text variants to deemphasize non-primary
         *   controls rather than color. most lingering instances of secondary should be switch to primary, and
         *   once we're off secondary, maybe we'll bring it back as a gray as another deemphasis option. we _do_
         *   use 'text.secondary' for minor text.
         * - info and tip are mostly just for alert severities. you can use `color: 'info'` for e.g. icons if
         *   the reason they're blue is because they provide extra info, but prefer primary for things that
         *   can be interacted with.
         */
        primary: {
            main: blue[400], // sofar blue
        },
        secondary: {
            main: blue[400], // sofar blue
        },
        tip: {
            main: blue[400], // sofar blue
        },
        info: {
            main: blue[400], // sofar blue
        },
        text: {
            secondary: minorTextGrey,
        },
    },
    typography: {
        fontFamily,
        fontWeightRegular: 300,
        fontWeightBold: boldWeight,
        h1: {
            fontSize: '3rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        h2: {
            fontSize: '2.25rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        h4: {
            fontSize: '1.125rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        h5: {
            fontSize: '1rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: boldWeight,
            lineHeight: 1.3,
        },
        body1: { lineHeight: 1.3, fontSize: '1rem' },
        body2: { lineHeight: 1.3, fontSize: '0.875rem' },
        caption: { lineHeight: 1, fontSize: '0.75rem' },
        button: {
            textTransform: 'unset',
        },
        minorLabel: {
            fontFamily,
            fontSize: '0.875rem',
            fontWeight: 300,
            color: minorTextGrey,
            lineHeight: 1.3,
        },
    },
    components: {
        MuiLink: {
            defaultProps: {
                component: LinkBehavior,
            } as LinkProps,
            styleOverrides: {
                root: {
                    '&:hover': { color: '#0A4C8D' },
                    variants: [
                        {
                            props: { color: 'text.secondary' },
                            style: {
                                '&:hover': { color: grey[800] },
                            },
                        },
                    ],
                },
            },
        },
        MuiButton: {
            defaultProps: {
                LinkComponent: LinkBehavior,
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textAlign: 'center',
                    borderRadius: 0,
                    variants: [
                        {
                            props: { variant: 'text' },
                            style: {
                                textAlign: 'unset',
                                lineHeight: 1.2,
                                paddingTop: '8px',
                                paddingBottom: '8px',
                            },
                        },
                        {
                            props: { variant: 'contained', color: 'tip' },
                            style: {
                                color: 'white',
                            },
                        },
                    ],
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { lineHeight: '1em' },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                variant: 'h4',
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    ':has(.readOnly)': {
                        pointerEvents: 'none',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: { fontSize: '1rem' },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.75rem',
                },
            },
            defaultProps: {
                arrow: true,
                slotProps: {
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -8],
                                },
                            },
                        ],
                        sx: { borderRadius: 0 },
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    strong: {
                        fontWeight: boldWeight,
                    },
                },
            },
        },
        MuiSwitch: {
            defaultProps: {
                slotProps: {
                    thumb: {
                        children: (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyItems: 'center',
                                    height: 1,
                                    width: 1,
                                    '.MuiSwitch-sizeSmall &': {
                                        position: 'relative',
                                        left: '-1px',
                                    },
                                }}
                            >
                                <CloseIcon
                                    sx={{
                                        fill: '#E6E0E9',
                                        height: 0.8,
                                        width: 0.8,
                                        px: '2px',
                                        '.Mui-checked &': { display: 'none' },
                                    }}
                                />
                                <CheckIcon
                                    sx={{
                                        fill: 'currentColor',
                                        height: 0.8,
                                        width: 0.8,
                                        px: '2px',
                                        display: 'none',
                                        '.Mui-checked &': { display: 'inline-block' },
                                    }}
                                />
                            </Box>
                        ),
                    },
                },
            },
            styleOverrides: {
                root: {
                    '&.MuiSwitch-sizeSmall': { padding: '1px' },
                    '&.MuiSwitch-sizeMedium': { padding: '6px' },
                },
                track: {
                    opacity: 1,
                    borderRadius: '13px',
                    backgroundColor: '#E6E0E9',
                    border: '2px solid #79747E',
                    boxSizing: 'border-box',

                    '.MuiSwitch-root .MuiSwitch-switchBase.Mui-checked+&': {
                        opacity: 1,
                        border: 'none',
                    },
                    '.MuiSwitch-root .MuiSwitch-switchBase.Mui-disabled+&': {
                        opacity: 0.4,
                    },
                    '.MuiSwitch-root .MuiSwitch-switchBase.Mui-checked.Mui-disabled+&': {
                        opacity: 0.4,
                    },
                },
                thumb: {
                    boxShadow: 'none',
                    backgroundColor: '#79747E',

                    '.Mui-checked &': {
                        backgroundColor: 'white',
                    },
                    '.Mui-disabled &': {
                        opacity: 0.3,
                    },
                    '.Mui-disabled.Mui-checked &': {
                        opacity: 1,
                    },
                },
            },
        },
    },
});

window.theme = theme;

export default theme;
